import { useEffect, useState } from 'react';
import { useLazyGetAiWorkerByIdQuery } from '@circuitry-ai/doc-data';

export interface UseUxgenieTemplateIdForAgentResult {
    /** aiworker_usage_id of the agent's configured UXGenie template, once resolved */
    templateId: string;
    isFetching: boolean;
    error: string | null;
}

/**
 * Resolves the `uxgenie_template` attribute configured on an agent (aiworker_id)
 * to the aiworker_usage_id of the template that should be rendered for it.
 *
 * Pass `enabled` as false until any precondition the caller needs (e.g. an auth
 * token being present in the store) is actually satisfied — this hook fires its
 * request as soon as it's enabled, with no internal wait/retry of its own.
 */
export function useUxgenieTemplateIdForAgent(
    aiworkerId: string | undefined | null,
    enabled = true
): UseUxgenieTemplateIdForAgentResult {
    const [getAiWorkerById, { isFetching }] = useLazyGetAiWorkerByIdQuery();
    const [templateId, setTemplateId] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!enabled || !aiworkerId) {
            return;
        }
        setError(null);
        getAiWorkerById(aiworkerId)
            .then((result: any) => {
                if (result?.error) {
                    console.error('Failed to fetch agent for UXGenie template:', result.error);
                    setError('Failed to resolve UXGenie template');
                    return;
                }
                const agent = result?.data;
                if (!agent) return;
                const attrs = agent?.aiworkers_attrs ?? [];
                const uxgenieAttr = attrs.find((attr: any) => attr?.attr_name === 'uxgenie_template');
                const id = uxgenieAttr?.attr_value?.trim();
                if (id) {
                    setTemplateId(id);
                } else {
                    setError('No UXGenie template configured for this agent');
                }
            })
            .catch((err) => {
                console.error('Failed to fetch agent for UXGenie template:', err);
                setError('Failed to resolve UXGenie template');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiworkerId, enabled, getAiWorkerById]);

    return { templateId, isFetching, error };
}
